import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, ShieldCheck, Ruler, Cake, Palette, Venus } from "lucide-react";
import { getPuppy, getPaymentsConfig } from "../lib/api";

const PAYPAL_ACTION = "https://www.paypal.com/cgi-bin/webscr";

export default function PuppyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [puppy, setPuppy] = useState(null);
  const [config, setConfig] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const paid = params.get("paid") === "1";

  useEffect(() => {
    getPuppy(id).then(setPuppy).catch(() => navigate("/puppies"));
    getPaymentsConfig().then(setConfig).catch(() => setConfig({ paypalEnabled: false }));
  }, [id, navigate]);

  if (!puppy) {
    return <div className="pt-40 text-center text-[#8a7a68]" data-testid="puppy-loading">Loading…</div>;
  }

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const price = Number(puppy.price || 0).toFixed(2);

  const specs = [
    { icon: Cake, label: "Age", value: puppy.age },
    { icon: Ruler, label: "Weight", value: puppy.weight },
    { icon: Venus, label: "Gender", value: puppy.gender },
    { icon: Palette, label: "Color", value: puppy.color },
  ].filter((s) => s.value);

  const adopted = puppy.status === "adopted";

  return (
    <div data-testid="puppy-detail-page" className="pt-28 md:pt-32">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <Link to="/puppies" data-testid="back-to-shop" className="inline-flex items-center gap-2 text-sm text-[#8a7a68] hover:text-[#2A2118] transition-colors mb-8">
          <ArrowLeft size={16} /> All puppies
        </Link>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* GALLERY */}
          <div>
            <motion.div
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="relative overflow-hidden rounded-md aspect-[4/5] bg-[#F5E6E3]"
            >
              <div className="absolute inset-0 spotlight z-10 pointer-events-none" />
              <img src={puppy.images?.[activeImg]} alt={puppy.name} className="h-full w-full object-cover" />
              {adopted && (
                <div className="absolute inset-0 flex items-center justify-center z-20">
                  <span className="adopted-stamp px-8 py-3 text-3xl md:text-4xl font-medium">ADOPTED</span>
                </div>
              )}
            </motion.div>
            {puppy.images?.length > 1 && (
              <div className="flex gap-3 mt-4">
                {puppy.images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    data-testid={`thumb-${i}`}
                    className={`h-20 w-20 rounded-sm overflow-hidden border-2 transition-colors ${activeImg === i ? "border-[#C8A98E]" : "border-transparent"}`}
                  >
                    <img src={img} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* INFO + CHECKOUT */}
          <div>
            <p className="label-eyebrow text-[#C8A98E] mb-3">{puppy.breed}</p>
            <h1 className="font-heading text-5xl md:text-6xl font-light text-[#2A2118] leading-none">{puppy.name}</h1>
            <p className="font-heading text-3xl text-[#2A2118] mt-4">${puppy.price?.toLocaleString()}</p>
            <p className="text-[#5c5245] font-light leading-relaxed mt-6">{puppy.description}</p>

            {specs.length > 0 && (
              <div className="grid grid-cols-2 gap-4 mt-8">
                {specs.map((s) => (
                  <div key={s.label} className="flex items-center gap-3 border border-[#E2D1C3] rounded-sm px-4 py-3">
                    <s.icon size={18} className="text-[#C8A98E]" />
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-[#8a7a68]">{s.label}</p>
                      <p className="text-sm text-[#2A2118]">{s.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-2 mt-6 text-sm text-[#5c5245]">
              <ShieldCheck size={18} className="text-[#C8A98E]" />
              Includes written health guarantee &amp; travel-ready vet clearance.
            </div>

            {/* CHECKOUT AREA */}
            <div className="mt-10 border-t border-[#E2D1C3] pt-8">
              {adopted ? (
                <div data-testid="checkout-adopted" className="bg-[#F5E6E3] rounded-md p-8 text-center">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#2A2118] text-[#FDFBF7] mb-4">
                    <Check size={22} />
                  </div>
                  <h3 className="font-heading text-2xl text-[#2A2118]">Already adopted</h3>
                  <p className="text-[#5c5245] font-light mt-2">{puppy.name} has found a forever home. Explore our other little ones.</p>
                  <Link to="/puppies" className="inline-block mt-6 text-sm text-[#2A2118] border-b border-[#C8A98E] pb-1">View more puppies</Link>
                </div>
              ) : paid ? (
                <div data-testid="checkout-thankyou" className="bg-[#F5E6E3] rounded-md p-8 text-center">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#2A2118] text-[#FDFBF7] mb-4">
                    <Check size={22} />
                  </div>
                  <h3 className="font-heading text-2xl text-[#2A2118]">Thank you!</h3>
                  <p className="text-[#5c5245] font-light mt-2">
                    We&apos;ve received your PayPal payment for {puppy.name}. We&apos;ll confirm and email you shortly to arrange delivery.
                  </p>
                  <Link to="/contact" className="inline-block mt-6 text-sm text-[#2A2118] border-b border-[#C8A98E] pb-1">Questions? Contact us</Link>
                </div>
              ) : config?.paypalEnabled ? (
                <form action={PAYPAL_ACTION} method="post" target="_top" data-testid="paypal-checkout">
                  <input type="hidden" name="cmd" value="_xclick" />
                  <input type="hidden" name="business" value={config.paypalEmail} />
                  <input type="hidden" name="item_name" value={`${puppy.name} — ${puppy.breed}`} />
                  <input type="hidden" name="item_number" value={puppy.id} />
                  <input type="hidden" name="amount" value={price} />
                  <input type="hidden" name="currency_code" value={config.currency || "USD"} />
                  <input type="hidden" name="no_note" value="0" />
                  <input type="hidden" name="return" value={`${origin}/puppy/${puppy.id}?paid=1`} />
                  <input type="hidden" name="cancel_return" value={`${origin}/puppy/${puppy.id}`} />
                  <input type="hidden" name="rm" value="2" />
                  <p className="label-eyebrow text-[#8a7a68] mb-4">Secure Checkout &middot; Powered by PayPal</p>
                  <button
                    type="submit"
                    data-testid="paypal-pay-btn"
                    className="w-full bg-[#2A2118] text-[#FDFBF7] py-4 rounded-sm text-sm tracking-wide hover:bg-[#3d3226] transition-colors inline-flex items-center justify-center gap-2"
                  >
                    Check Out with PayPal &mdash; ${puppy.price?.toLocaleString()}
                  </button>
                  <p className="text-xs text-[#8a7a68] mt-3 text-center">
                    You&apos;ll be redirected to PayPal to complete your payment securely. No PayPal account required — pay with any card.
                  </p>
                </form>
              ) : (
                <div data-testid="checkout-unconfigured" className="bg-[#F5E6E3] rounded-md p-8">
                  <h3 className="font-heading text-2xl text-[#2A2118]">Reserve {puppy.name}</h3>
                  <p className="text-[#5c5245] font-light mt-2 mb-6">
                    To reserve {puppy.name} today, contact us and we&apos;ll guide you through a safe payment and delivery.
                  </p>
                  <Link to="/contact" data-testid="reserve-contact-btn" className="bg-[#2A2118] text-[#FDFBF7] px-7 py-3.5 rounded-sm text-sm hover:bg-[#3d3226] transition-colors">
                    Reserve via Contact
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
